import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, AlertTriangle, Gauge, Layers3 } from 'lucide-react';

const AssessmentTracker = ({ used, total, plan }) => {
  const hasUnlimitedAccess = total >= 999999;
  const percentage = hasUnlimitedAccess ? 0 : total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const remaining = hasUnlimitedAccess ? 'Unlimited' : Math.max(total - used, 0);
  const isAtLimit = !hasUnlimitedAccess && total > 0 && used >= total;
  const isNearLimit = !isAtLimit && !hasUnlimitedAccess && percentage >= 80;

  return (
    <motion.section
      className="dashboard-glass rounded-xl p-5 sm:p-6"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.38 }}
      data-testid="assessment-usage-panel"
    >
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-sky-400/25 bg-sky-400/10">
            <Gauge className="h-5 w-5 text-sky-300" />
          </div>
          <div>
            <p className="dashboard-eyebrow">Assessment capacity</p>
            <h2 className="mt-1 font-heading text-xl font-medium text-white" data-testid="assessment-usage-plan">
              {plan === 'pro' ? 'Pro' : 'Standard'} workspace
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-5 sm:text-right">
          <div data-testid="assessment-usage-used"><p className="font-heading text-xl font-semibold text-white">{used}</p><p className="text-[11px] text-slate-500">Used</p></div>
          <div data-testid="assessment-usage-total"><p className="font-heading text-xl font-semibold text-white">{hasUnlimitedAccess ? '∞' : total}</p><p className="text-[11px] text-slate-500">Capacity</p></div>
          <div data-testid="assessment-usage-remaining"><p className={`font-heading text-xl font-semibold ${isAtLimit ? 'text-red-300' : isNearLimit ? 'text-amber-300' : 'text-emerald-300'}`}>{hasUnlimitedAccess ? '∞' : remaining}</p><p className="text-[11px] text-slate-500">Remaining</p></div>
        </div>
      </div>

      <div className="mt-6" data-testid="assessment-usage-progress">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-slate-400">Plan utilization</span>
          <span className="font-mono text-slate-300">{hasUnlimitedAccess ? 'Unlimited access' : `${Math.round(percentage)}%`}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-800">
          <motion.div
            className={`h-full rounded-full ${isAtLimit ? 'bg-red-400' : isNearLimit ? 'bg-amber-400' : 'bg-emerald-400'}`}
            initial={{ width: 0 }}
            animate={{ width: hasUnlimitedAccess ? '100%' : `${percentage}%` }}
            transition={{ duration: 1.1, ease: 'easeOut' }}
          />
        </div>
      </div>

      {(isAtLimit || isNearLimit || hasUnlimitedAccess) && (
        <div className={`mt-5 flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ${isAtLimit ? 'border-red-400/20 bg-red-400/10 text-red-200' : isNearLimit ? 'border-amber-400/20 bg-amber-400/10 text-amber-200' : 'border-emerald-400/20 bg-emerald-400/10 text-emerald-200'}`} data-testid="assessment-usage-notice">
          {isAtLimit ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> : hasUnlimitedAccess ? <Layers3 className="mt-0.5 h-4 w-4 shrink-0" /> : <ClipboardCheck className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{isAtLimit ? 'Assessment capacity reached. Request more capacity to continue.' : hasUnlimitedAccess ? 'Your Product Owner workspace has unlimited assessment capacity.' : 'You are nearing your assessment capacity.'}</span>
        </div>
      )}
    </motion.section>
  );
};

export default AssessmentTracker;